import moment from "moment";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./dateRange";
import { normalizeSalesCharts, deriveByClientSeries } from "./salesCharts";

// Factory del módulo Vuex de "Reporte de <X>" (tabla de evolución + gráficos del
// header + comparación año-contra-año). Costos y Gastos comparten el ~90% del
// módulo: mismo state, mutations, getters de tabla/charts y la misma mecánica de
// fetch. Difieren sólo en endpoints, el ratio operacional (cuentas L2 + label),
// el label de la fila Total, los nombres de las actions (que los tabs despachan),
// el prefijo de log y los mensajes de error.
//
// @param {object}   config
// @param {object}   config.apiFns           { byLevels, charts, comparison }
// @param {string[]} config.ratioL2Accounts  cuentas L2 que suman el numerador del ratio
// @param {string}   config.ratioLabel       label de la fila ratio ("% Costos / Ventas")
// @param {string}   config.totalLabel       label de la fila Total ("Costos Totales")
// @param {object}   config.fetchActionNames { data, charts, comparison }
// @param {string}   config.logPrefix        "[costsReport]" / "[expensesReport]"
// @param {object}   config.errorMessages    { data, charts } (comparison es común)
export function createReportModule({
    apiFns,
    ratioL2Accounts,
    ratioLabel,
    totalLabel,
    fetchActionNames,
    logPrefix,
    errorMessages,
}) {
    const state = () => ({
        /**
         * Dimensión activa del header: "cuentas" (plan de cuentas), el nombre de
         * una categoría de clasificación (tiendas, marcas, …) o "contrapartes"
         * (breakdown por tipo de contraparte). Define el group_by de tabla y
         * gráficos.
         */
        activeSubFilter: "cuentas",
        selectedCounterpartTypes: [],
        /**
         * Filtro multi-select del header: etiquetas L3 (cuentas o contrapartes)
         * elegidas. Vacío = sin filtro (se muestran todas).
         */
        selectedItems: [],
        monthHeaders: [],
        accountsData: {},
        /**
         * Monto del mismo bucket 12 meses atrás, por etiqueta (L2/L3) y período.
         * Alimenta el tooltip YoY de la tabla. `null` cuando no hay peer hace 12m.
         */
        prevYearData: {},
        /**
         * Monto del período inmediatamente anterior (peer MoM/QoQ), por etiqueta
         * (L2/L3) y período. Alimenta la línea MoM/QoQ del tooltip dándole peer
         * real a la primera columna visible. `null` cuando no hay peer. Vacío
         * en year/ytd (no aplica período-sobre-período).
         */
        prevPeriodData: {},
        level2Accounts: [],
        level3Accounts: [],
        hierarchy: {},
        /**
         * Tipo de contraparte representativo por label (alias), sólo poblado en
         * modo contrapartes. El tab lo usa para abrir el detalle de la contraparte
         * (modal 2) con la obligación correcta.
         */
        counterpartTypes: {},
        /**
         * Categorías (tipos de atributo) con clasificación real en el período —
         * confirmadas por el backend. El header deshabilita los chips de
         * categoría que no estén acá (sólo tendrían "Sin <categoría>").
         * @type {string[]}
         */
        availableCategories: [],
        salesByPeriod: {},
        /**
         * Acumulado del periodo + comparación contra ventana previa de igual largo.
         * Indexado por L2/L3 + clave reservada "__total__".
         * `pct` es null cuando prevAcc === 0 (frontend muestra "—").
         */
        accumulatedData: {},
        periodRange: { init_date: null, end_date: null },
        isLoading: false,
        error: null,
        /**
         * Series de los dos gráficos del header (stacked + líneas por contraparte/
         * cuenta). Normalizado por `normalizeSalesCharts` (mismo shape que Ventas
         * para reusar SalesChartsRow). null antes del primer fetch.
         */
        chartsData: null,
        isChartsLoading: false,
        chartsError: null,
        /**
         * Comparación año-contra-año (gráfico que reemplaza la tabla al togglear
         * "Comparación"). `comparisonData` = { months, series, availableYears }.
         * `comparisonYears` = años elegidos (default [añoActual, añoActual-1]).
         */
        comparisonData: null,
        isComparisonLoading: false,
        comparisonError: null,
        comparisonYears: [],
    });

    const EMPTY_ACC = { acc: 0, prevAcc: 0, pct: null };

    const getters = {
        isCuentasMode: (state) => state.activeSubFilter === "cuentas",

        // group_by que comparten la tabla y los gráficos del header. "cuentas" o
        // el nombre de la categoría se mandan tal cual; en contrapartes mandamos
        // los tipos seleccionados unidos por coma (todos por default vía
        // select-all-on-open del dropdown).
        groupByParam: (state) => {
            if (state.activeSubFilter === "contrapartes") {
                return state.selectedCounterpartTypes.join(",");
            }
            return state.activeSubFilter;
        },

        // Payload de los gráficos del header, listo para SalesChartsRow. Sin
        // selección usa la serie top-N + "Otros" del endpoint `charts`. Con
        // selección activa deriva las series client-side de `accountsData`.
        headerChartsData: (state) => {
            if (!state.selectedItems.length) return state.chartsData;
            return deriveByClientSeries(
                state.accountsData,
                state.monthHeaders,
                state.selectedItems,
            );
        },

        tableRows: (state) => {
            // En contrapartes la tabla es plana: una fila por contraparte, sin
            // anidar bajo cuentas. report_data/accumulatedData ya agregan cada
            // alias entre las cuentas en que aparece; iteramos level3Accounts
            // (deduplicado) y ordenamos por acumulado descendente. level 1 = fila
            // plana (el row component no la indenta ni le pone chevron).
            if (state.activeSubFilter === "contrapartes") {
                return state.level3Accounts
                    .map((label) => {
                        const acc = state.accumulatedData[label] ?? EMPTY_ACC;
                        return {
                            label,
                            level: 1,
                            isTotal: false,
                            values: state.monthHeaders.map(
                                (header) => state.accountsData[label]?.[header] ?? 0,
                            ),
                            prevYearValues: state.monthHeaders.map(
                                (header) => state.prevYearData[label]?.[header] ?? null,
                            ),
                            prevPeriodValues: state.monthHeaders.map(
                                (header) => state.prevPeriodData[label]?.[header] ?? null,
                            ),
                            accumulated: acc.acc,
                            pct: acc.pct,
                        };
                    })
                    // Por monto acumulado del rango desc, desempate alfabético
                    // (mismo criterio -monto/nombre que charts y Ventas).
                    .sort((a, b) => (b.accumulated - a.accumulated) || a.label.localeCompare(b.label));
            }

            const rows = [];
            state.level2Accounts.forEach((l2) => {
                const l2Acc = state.accumulatedData[l2] ?? EMPTY_ACC;
                rows.push({
                    label: l2,
                    level: 2,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[l2]?.[header] ?? 0,
                    ),
                    prevYearValues: state.monthHeaders.map(
                        (header) => state.prevYearData[l2]?.[header] ?? null,
                    ),
                    prevPeriodValues: state.monthHeaders.map(
                        (header) => state.prevPeriodData[l2]?.[header] ?? null,
                    ),
                    accumulated: l2Acc.acc,
                    pct: l2Acc.pct,
                });

                const children = state.hierarchy[l2] || [];
                children.forEach((l3) => {
                    if (l3 === l2) return;
                    const l3Acc = state.accumulatedData[l3] ?? EMPTY_ACC;
                    rows.push({
                        label: l3,
                        level: 3,
                        parent: l2,
                        isTotal: false,
                        values: state.monthHeaders.map(
                            (header) => state.accountsData[l3]?.[header] ?? 0,
                        ),
                        prevYearValues: state.monthHeaders.map(
                            (header) => state.prevYearData[l3]?.[header] ?? null,
                        ),
                        prevPeriodValues: state.monthHeaders.map(
                            (header) => state.prevPeriodData[l3]?.[header] ?? null,
                        ),
                        accumulated: l3Acc.acc,
                        pct: l3Acc.pct,
                    });
                });
            });
            return rows;
        },

        totalRow: (state) => {
            const values = state.monthHeaders.map((header) =>
                state.level2Accounts.reduce((sum, account) => {
                    const val = Number(state.accountsData[account]?.[header] || 0);
                    return sum + (isNaN(val) ? 0 : val);
                }, 0),
            );

            // Peer del total por bucket = suma sobre las L2 (año anterior para
            // YoY, período anterior para MoM/QoQ). null cuando ninguna L2 tenía
            // peer en ese bucket.
            const sumPeer = (header, dict) => {
                let sum = 0;
                let anyPeer = false;
                state.level2Accounts.forEach((account) => {
                    const peer = dict[account]?.[header];
                    if (peer != null) {
                        sum += peer;
                        anyPeer = true;
                    }
                });
                return anyPeer ? sum : null;
            };
            const prevYearValues = state.monthHeaders.map((h) => sumPeer(h, state.prevYearData));
            const prevPeriodValues = state.monthHeaders.map((h) => sumPeer(h, state.prevPeriodData));

            const totals = state.accumulatedData.__total__ ?? EMPTY_ACC;
            return {
                label: totalLabel,
                values,
                prevYearValues,
                prevPeriodValues,
                isTotal: true,
                accumulated: totals.acc,
                pct: totals.pct,
            };
        },

        /**
         * Ratio costos/gastos operacionales / ventas netas por período. Numerador
         * = suma de las level_2 en `ratioL2Accounts` (no usa totalRow porque ese
         * podría incluir cuentas no operacionales, inconsistente con el ratio del
         * Estado de Resultados). Valores como ratio decimal (0.12 = 12%); la UI lo
         * formatea con formatPercent.
         */
        ratioRow: (state) => {
            const values = state.monthHeaders.map((header) => {
                const total = ratioL2Accounts.reduce((sum, account) => {
                    const val = Number(state.accountsData[account]?.[header] || 0);
                    return sum + (isNaN(val) ? 0 : val);
                }, 0);
                const ventas = Number(state.salesByPeriod[header] || 0);
                return ventas !== 0 ? total / ventas : 0;
            });
            return { label: ratioLabel, values, isRatio: true };
        },
    };

    const mutations = {
        SET_ACTIVE_SUB_FILTER(state, filter) {
            state.activeSubFilter = filter;
            // Cambia la dimensión → el filtro de items deja de aplicar. Fuera de
            // contrapartes limpiamos también los tipos, así reentrar a esa
            // dimensión arranca con todos marcados (select-all-on-open del dropdown).
            state.selectedItems = [];
            if (filter !== "contrapartes") state.selectedCounterpartTypes = [];
        },
        SET_SELECTED_COUNTERPART_TYPES(state, types) {
            state.selectedCounterpartTypes = Array.isArray(types) ? [...types] : [];
            // Elegir tipos = dimensión contrapartes; vaciar = volver a cuentas
            // (misma semántica histórica donde [] significaba modo cuentas).
            state.activeSubFilter = state.selectedCounterpartTypes.length
                ? "contrapartes"
                : "cuentas";
            state.selectedItems = [];
        },
        SET_SELECTED_ITEMS(state, items) {
            state.selectedItems = Array.isArray(items) ? [...items] : [];
        },
        SET_REPORT_DATA(state, { level2Accounts, level3Accounts, hierarchy, counterpartTypes, availableCategories, accountsData, prevYearData, prevPeriodData, monthHeaders, periodRange, salesByPeriod, accumulatedData }) {
            state.level2Accounts = level2Accounts;
            state.level3Accounts = level3Accounts;
            state.hierarchy = hierarchy;
            state.counterpartTypes = counterpartTypes || {};
            state.availableCategories = availableCategories || [];
            state.accountsData = accountsData;
            state.prevYearData = prevYearData || {};
            state.prevPeriodData = prevPeriodData || {};
            state.monthHeaders = monthHeaders;
            state.periodRange = periodRange;
            state.salesByPeriod = salesByPeriod || {};
            state.accumulatedData = accumulatedData || {};
        },
        SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
        SET_ERROR(state, error) { state.error = error; },
        SET_CHARTS_DATA(state, payload) { state.chartsData = payload; },
        SET_CHARTS_LOADING(state, isLoading) { state.isChartsLoading = isLoading; },
        SET_CHARTS_ERROR(state, error) { state.chartsError = error; },
        SET_COMPARISON_DATA(state, payload) { state.comparisonData = payload; },
        SET_COMPARISON_LOADING(state, isLoading) { state.isComparisonLoading = isLoading; },
        SET_COMPARISON_ERROR(state, error) { state.comparisonError = error; },
        SET_COMPARISON_YEARS(state, years) { state.comparisonYears = years; },
    };

    const actions = {
        // Tabla de evolución: report_data por cuenta/contraparte + acumulados +
        // ventas por período (para el ratio). Mismo rango/granularidad/dimensión
        // que los gráficos (groupByParam).
        async [fetchActionNames.data]({ getters, commit, rootState }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const endMonth = rootState.reportsFilters.endMonth;
                const filterPeriod = rootState.financialReports.filterPeriod;

                const { initDate, endDate } = resolveDateRange(
                    endMonth,
                    rootState.reportsFilters.selectedPeriodRange,
                    filterPeriod,
                    rootState.reportsFilters.customRange,
                    rootState.reportsFilters.customYearRange,
                );

                const response = await apiFns.byLevels(initDate, endDate, getters.groupByParam, filterPeriod);
                const {
                    report_data,
                    level2_accounts,
                    level3_accounts,
                    hierarchy,
                    counterpart_types,
                    available_categories,
                    sales_by_period,
                    accumulated_data: accumulatedRaw,
                } = response.data;

                const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);
                const accountsData = {};
                const prevYearData = {};
                const prevPeriodData = {};

                Object.keys(report_data).forEach((accountName) => {
                    accountsData[accountName] = {};
                    prevYearData[accountName] = {};
                    prevPeriodData[accountName] = {};
                    report_data[accountName].forEach((row) => {
                        const header = formatPeriodLabel(row.period, filterPeriod);
                        const amount = Number(row.total_amount);
                        accountsData[accountName][header] = isNaN(amount) ? 0 : amount;
                        const peer = row.prev_year_amount;
                        prevYearData[accountName][header] = peer == null ? null : Number(peer);
                        const periodPeer = row.prev_period_amount;
                        prevPeriodData[accountName][header] = periodPeer == null ? null : Number(periodPeer);
                    });
                });

                const salesByPeriod = {};
                (sales_by_period || []).forEach((row) => {
                    const header = formatPeriodLabel(row.period, filterPeriod);
                    const amount = Number(row.total_amount);
                    salesByPeriod[header] = isNaN(amount) ? 0 : amount;
                });

                // Normaliza snake→camel y castea a Number. Decimal puede llegar
                // como string desde algunos paths del selector.
                const accumulatedData = {};
                Object.entries(accumulatedRaw ?? {}).forEach(([key, value]) => {
                    accumulatedData[key] = {
                        acc: Number(value?.acc ?? 0),
                        prevAcc: Number(value?.prev_acc ?? 0),
                        pct: value?.pct == null ? null : Number(value.pct),
                    };
                });

                commit("SET_REPORT_DATA", {
                    level2Accounts: level2_accounts,
                    level3Accounts: level3_accounts,
                    hierarchy,
                    counterpartTypes: counterpart_types || {},
                    availableCategories: available_categories || [],
                    accountsData,
                    prevYearData,
                    prevPeriodData,
                    monthHeaders,
                    periodRange: { init_date: initDate, end_date: endDate },
                    salesByPeriod,
                    accumulatedData,
                });
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
                commit("SET_ERROR", errorMessages.data);
            } finally {
                commit("SET_LOADING", false);
            }
        },

        // Gráficos del header (stacked + líneas por contraparte/cuenta/categoría).
        // Mismo rango/granularidad y dimensión que la tabla (groupByParam).
        // Normalización compartida con Ventas.
        async [fetchActionNames.charts]({ getters, commit, rootState }, payload = {}) {
            commit("SET_CHARTS_LOADING", true);
            commit("SET_CHARTS_ERROR", null);

            try {
                const endMonth = rootState.reportsFilters.endMonth;
                const filterPeriod = rootState.financialReports.filterPeriod;
                const { initDate, endDate } = resolveDateRange(
                    endMonth,
                    rootState.reportsFilters.selectedPeriodRange,
                    filterPeriod,
                    rootState.reportsFilters.customRange,
                    rootState.reportsFilters.customYearRange,
                );

                const response = await apiFns.charts(
                    initDate,
                    endDate,
                    filterPeriod,
                    payload.topN,
                    getters.groupByParam,
                );
                commit("SET_CHARTS_DATA", normalizeSalesCharts(response.data, filterPeriod));
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
                commit("SET_CHARTS_ERROR", extractErrorMessage(error, errorMessages.charts));
            } finally {
                commit("SET_CHARTS_LOADING", false);
            }
        },

        // Comparación año-contra-año del total (lazy: el tab la dispara al abrir el
        // panel y al cambiar el mes mientras está abierto). El año en curso lo
        // recorta el backend al mes del header.
        async [fetchActionNames.comparison]({ state, commit, rootState }) {
            commit("SET_COMPARISON_LOADING", true);
            commit("SET_COMPARISON_ERROR", null);

            try {
                const endMonth = rootState.reportsFilters.endMonth;
                const endYear = moment(endMonth).year();

                let years = state.comparisonYears;
                if (!years || years.length === 0) {
                    years = [endYear, endYear - 1];
                    commit("SET_COMPARISON_YEARS", years);
                }

                const endDate = moment(endMonth).endOf("month").format("YYYY-MM-DD");
                const response = await apiFns.comparison(endDate, years);
                const {
                    months,
                    series,
                    available_years: availableYears,
                } = response.data;

                commit("SET_COMPARISON_DATA", Object.freeze({
                    months: Object.freeze(months || []),
                    series: Object.freeze((series || []).map(Object.freeze)),
                    availableYears: Object.freeze(availableYears || []),
                }));
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
                commit("SET_COMPARISON_ERROR", extractErrorMessage(error, "Error al cargar la comparación."));
            } finally {
                commit("SET_COMPARISON_LOADING", false);
            }
        },
    };

    return { namespaced: true, state, getters, mutations, actions };
}
